import { useEffect, useState } from 'react';
import { qaCompareSimpleValue, CompareStatus } from '../../../../utils';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import OrgNrLeft from './OrgNrLeft';
import OrgNrRight from './OrgNrRight';
import { OrgNrInfo } from './types';
import querystring from 'query-string';
import openInNewTab from 'utils/functions/openInNewTab';
import { EnhetSearchFilter } from 'services/KjederegisteretAdminApi/EnhetSearchApi/types';
import { Button, Collapse, Row } from 'react-bootstrap';
import cx from 'classnames';
import Style from '../../index.module.css';
import CompareIcon from '../../../CompareIcon';
import { IcebergIcon, useLocalStorage } from '@tradesolution/iceberg-ui-react';
import useBrregLookup from 'utils/hooks/useBrregLookup';
import OrgNrCompareCell from './OrgNrCompareCell';

interface Props {
  changeRequestId: string;
  /** Brukes for dataene som kommer fra QAApi  */
  incomingChangeRequest: OrgNrInfo;
  /** Brukes for datene fra vår databasen */
  outgoingChangeRequest: OrgNrInfo;
  /** Kjøres når et nytt orgnummer er valgt */
  onChange: (updatedValue: OrgNrInfo) => void;
  enhetJurNavn: string;
  duplicateWarning: string;
}

const OrgNrRow = (props: Props) => {
  const isMountedRef = useIsMountedRef();

  const [manuallyExpanded, setManuallyExpanded] = useLocalStorage<boolean>(
    'orgnrrow-manual-expand',
    false,
  );
  const [isExpandRow, setIsExpandRow] = useState<boolean>(manuallyExpanded);
  const [leftNeedsAttention, setLeftNeedsAttention] = useState(false);
  const [rightNeedsAttention, setRightNeedsAttention] = useState(false);
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(
      props.incomingChangeRequest.organisasjonsnr,
      props.outgoingChangeRequest.organisasjonsnr,
    ),
  );

  // Single Brreg lookup for incoming orgnr shared by left and compare cell
  const { brregInfo } = useBrregLookup(props.incomingChangeRequest.organisasjonsnr);

  const handleGoToEnhetSearch = (criteria: string) => {
    let filter = new EnhetSearchFilter();
    filter.criteria = criteria;
    let searchstring = querystring.stringify(filter, {
      arrayFormat: 'bracket',
      skipNull: true,
    });
    openInNewTab(`/enheter?${searchstring}`);
  };

  // update compare result
  useEffect(() => {
    if (isMountedRef.current) {
      setCompareStatus(
        qaCompareSimpleValue(
          props.incomingChangeRequest.organisasjonsnr,
          props.outgoingChangeRequest?.organisasjonsnr,
        ),
      );
    }
  }, [props.outgoingChangeRequest.organisasjonsnr, props.incomingChangeRequest.organisasjonsnr]);

  const [jurdiskNavnUliktIBrregOgKregDb, setJurdiskNavnUliktIBrregOgKregDb] =
    useState<boolean>(false);

  // rad skal være ekspandert hvis det er info her som trenger oppmerksomhet
  // UNLESS user has manually expanded it (then keep it expanded always)
  useEffect(() => {
    if (manuallyExpanded) {
      setIsExpandRow(true);
      return;
    }
    // Auto-expand if there are attention flags
    const shouldAutoExpand =
      compareStatus === 'ulik' ||
      compareStatus === 'ny' ||
      leftNeedsAttention ||
      rightNeedsAttention;
    setIsExpandRow(shouldAutoExpand);
  }, [compareStatus, leftNeedsAttention, rightNeedsAttention, manuallyExpanded]);

  const handleExpandRowClick = () => {
    const newExpandState = !isExpandRow;
    setIsExpandRow(newExpandState);

    // If user is manually expanding, remember this in localStorage
    if (newExpandState) {
      setManuallyExpanded(true);
    } else {
      // If user collapses, clear the manual flag
      setManuallyExpanded(false);
    }
  };

  return (
    <>
      <div
        className={cx(Style.changeRequestRowHeaderContainer, {
          [Style.isNotExpandedRowContainer]: !isExpandRow,
        })}
        role="button"
        tabIndex={0}
        aria-expanded={isExpandRow}
        onClick={handleExpandRowClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleExpandRowClick();
          }
        }}
      >
        <h6 className={Style.changeRequestRowHeader}>
          {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
          <span className={Style.changeRequestRowHeaderText}>Organisasjonsnummer</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <OrgNrLeft
              incoming={props.incomingChangeRequest}
              compareStatus={compareStatus}
              brregInfo={brregInfo}
              onAttentionChange={(needs) => setLeftNeedsAttention(needs)}
            />
            <OrgNrCompareCell
              compareStatus={compareStatus}
              brregFirst={brregInfo}
              incomingOrgNr={props.incomingChangeRequest.organisasjonsnr}
              onTransferFromIncoming={(val: OrgNrInfo) => props.onChange(val)}
            />
            <OrgNrRight
              outgoing={props.outgoingChangeRequest}
              enhetJurNavn={props.enhetJurNavn}
              duplicateWarning={props.duplicateWarning}
              onChange={(val: OrgNrInfo) => props.onChange(val)}
              onAttentionChange={(needs) => setRightNeedsAttention(needs)}
              onSeeEnheter={(criteria) => handleGoToEnhetSearch(criteria)}
            />
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default OrgNrRow;
